import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSwipers, useSwiperMutations, uploadFile } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';

export default function SwiperPage() {
  const { data: swipers = [], isLoading } = useSwipers();
  const { createSwiper, updateSwiper, deleteSwiper } = useSwiperMutations();
  
  const [open, setOpen] = useState(false);
  const [editingSwiper, setEditingSwiper] = useState(null);
  const [formData, setFormData] = useState({
    imageUrl: '',
    displayOrder: 0,
    isActive: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleOpen = (swiper = null) => {
    // Reset file input when opening the dialog
    setSelectedFile(null);
    if (swiper) {
      setEditingSwiper(swiper);
      setFormData({
        imageUrl: resolveImageUrl(swiper.imageUrl),
        displayOrder: swiper.displayOrder,
        isActive: swiper.isActive
      });
    } else {
      setEditingSwiper(null);
      setFormData({
        imageUrl: '',
        displayOrder: 0,
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingSwiper(null);
    setSelectedFile(null);
    setIsUploading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Clear the URL field when a file is selected
      setFormData(prev => ({
        ...prev,
        imageUrl: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsUploading(true);
      
      if (!selectedFile && !formData.imageUrl) {
        throw new Error('Please upload an image');
      }

      const data = { ...formData };

      // Upload file if selected
      if (selectedFile) {
        const uploadResponse = await uploadFile(selectedFile);
        if (uploadResponse.status) {
          data.imageUrl = uploadResponse.data.url;
        } else {
          throw new Error(uploadResponse.message || 'Failed to upload image');
        }
      }
      
      if (data.imageUrl) {
        data.imageUrl = resolveImageUrl(data.imageUrl);
      }

      if (editingSwiper) {
        await updateSwiper.mutateAsync({
          id: editingSwiper.id,
          ...data,
          displayOrder: Number(data.displayOrder)
        });
        setSnackbar({ open: true, message: 'Swiper updated successfully', severity: 'success' });
      } else {
        await createSwiper.mutateAsync({
          ...data,
          displayOrder: Number(data.displayOrder)
        });
        setSnackbar({ open: true, message: 'Swiper created successfully', severity: 'success' });
      }
      handleClose();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || error.message || 'An error occurred', 
        severity: 'error' 
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this swiper?')) {
      try {
        await deleteSwiper.mutateAsync(id);
        setSnackbar({ open: true, message: 'Swiper deleted successfully', severity: 'success' });
      } catch (error) {
        setSnackbar({ 
          open: true, 
          message: error.response?.data?.message || 'Failed to delete swiper', 
          severity: 'error' 
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">Swiper Management</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add Swiper
          </Button>
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Display Order</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {swipers.map((swiper) => (
                    <TableRow key={swiper.id}>
                      <TableCell>
                        <Box 
                          component="img"
                          src={resolveImageUrl(swiper.imageUrl)}
                          alt="Swiper"
                          sx={{ width: 100, height: 'auto' }}
                        />
                      </TableCell>
                      <TableCell>{swiper.displayOrder}</TableCell>
                      <TableCell>
                        {swiper.isActive ? 'Active' : 'Inactive'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpen(swiper)} title="Edit">
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(swiper.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingSwiper ? 'Edit Swiper' : 'Add New Swiper'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    onChange={handleFileChange}
                    required={!editingSwiper}
                  />
                  <label htmlFor="image-upload">
                    <Button 
                      variant="outlined" 
                      component="span" 
                      fullWidth 
                      sx={{ mb: 2 }}
                      startIcon={<AddIcon />}
                    >
                      {selectedFile ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>
                  {selectedFile ? (
                    <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                      {selectedFile.name}
                    </Typography>
                  ) : formData.imageUrl ? (
                    <Box mt={1}>
                      <Typography variant="caption" display="block">Current image:</Typography>
                      <Box 
                        component="img" 
                        src={resolveImageUrl(formData.imageUrl)} 
                        alt="Current" 
                        sx={{ maxWidth: '100%', maxHeight: 100, mt: 1 }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Order"
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData(prev => ({
                          ...prev,
                          isActive: e.target.checked
                        }))
                      }
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={createSwiper.isLoading || updateSwiper.isLoading || isUploading}
            >
              {(createSwiper.isLoading || updateSwiper.isLoading || isUploading) ? (
                <CircularProgress size={24} />
              ) : editingSwiper ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
